import React, { ReactNode, useCallback } from 'react';

import { useSchool } from 'features/school';

import useGA from './hooks/useGA';
import useGA4 from './hooks/useGA4';
import useGTM from './hooks/useGTM';

import { TrackingContext } from './context';
import { EventsProps } from './types';
import { useResources } from 'features/resources';

interface GaProviderProps {
  children: ReactNode;
}

const TrackingProvider = ({ children }: GaProviderProps) => {
  const { schoolMelver, schoolUuid } = useSchool();

  const {
    schools: { schoolIsTopGain, schoolIsSirius },
  } = useResources();

  const schoolBenndorf = schoolUuid === 'cfaf550b-037d-45b2-9f84-979fb01185d4';
  const schoolG10 = schoolUuid === '14d970bd-3794-4268-bf67-2c5d2d0f0285';
  const schoolMeuPlanoCrypto = schoolUuid === '20b929ad-c773-46e2-93de-991988587835';
  // const schoolTradeAoVivo = schoolUuid === '85338cb1-8075-4dcf-afe2-6ce534dcde27';
  const schoolTermineSeusEstudos = schoolUuid === '76cebc79-bf6c-4144-bab2-947362f3f8c4';
  const school3xperiencia = schoolUuid === 'fe8e4d2e-30cd-41d8-8e32-ad3bcd79ad9a';
  const schoolDVInvest = schoolUuid === '519a0647-e8b4-48bc-9158-d5488d8036bd';
  const schoolB3 = schoolUuid === '2809e7aa-8dd8-4925-ac33-bc841cc4a4f0';

  let gaTIDs: string[] = [];
  let ga4TIDs: string[] = [];
  let gtmID: string | null = null;

  if (schoolMelver) {
    gtmID = 'GTM-TL85QLN';
    ga4TIDs = ['G-EDMZSFX81D', 'AW-985800468'];
  } else if (schoolMeuPlanoCrypto) {
    gaTIDs = ['UA-207666125-1'];
  } else if (schoolG10) {
    gaTIDs = ['UA-114744250-1'];
    ga4TIDs = ['AW-10800976383'];
    // } else if (schoolTradeAoVivo) {
    //   ga4TIDs = ['G-K3EPNCJ6ER'];
  } else if (schoolTermineSeusEstudos) {
    ga4TIDs = ['G-JNG5CWD77K', 'AW-1006691062'];
  } else if (school3xperiencia) {
    gtmID = 'GTM-WLC8MFG';
    ga4TIDs = ['AW-10928307223'];
  } else if (schoolBenndorf) {
    gaTIDs = ['UA-241475841-1'];
  } else if (schoolDVInvest) {
    ga4TIDs = ['AW-609791086'];
  } else if (schoolB3) {
    gtmID = 'GTM-WNBP8FK7';
    // ga4TIDs = [
    //   //'GTM-WHL25JWL', // B3 OLD
    //   'GTM-WNBP8FK7', // B3
    //   // 'GTM-MV2K6PZB', // MELVER
    // ];
  } else if (schoolIsTopGain) {
    gtmID = 'GTM-MSQCH6F';
  } else if (schoolIsSirius) {
    gtmID = 'GTM-T2TM6QS3';
  }

  const gaTracker = useGA(gaTIDs);
  const ga4Tracker = useGA4(ga4TIDs);
  useGTM(gtmID);

  const eventTracker = useCallback(
    ({ conversionLabel, ...props }: EventsProps, conversion = false) => {
      if (gaTracker) gaTracker.gaEventTrack(props);
      if (ga4Tracker) ga4Tracker.gaEventTrack(props);
      // if (gtmTracker) gtmTracker.gtmEventTrack({ eventName: 'click', ...props });

      if (schoolMelver) {
        if (conversion) {
          (window as any).gtag('event', 'conversion', {
            transactionId: props.transaction_id || undefined,
            value: props.value,
            customerEmail: props.customer_email || undefined,
            currency: 'BRL',
            productName: props.product_name || undefined,
            schoolAlias: props.schoolAlias || undefined,
          });
        }
      } else if (schoolG10) {
        if (conversion) {
          (window as any).gtag('event', 'conversion', {
            send_to: `AW-316343086${conversionLabel ? `/${conversionLabel}` : ''}`,
            value: props.value,
            currency: 'BRL',
            transaction_id: props.transaction_id || undefined,
          });
        }
      } else if (schoolMeuPlanoCrypto) {
        if (conversion) {
          (window as any).gtag('event', 'conversion', {
            send_to: 'AW-618982155/mNtGCMuypt4DEIvWk6cC',
            value: props.value,
            currency: 'BRL',
            transaction_id: props.transaction_id || undefined,
          });
        }
      } else if (schoolBenndorf) {
        if (conversion) {
          (window as any).gtag('event', 'conversion', {
            send_to: 'AW-346371971/0q3hCICn9d4DEIPvlKUB',
            value: props.value,
            currency: 'BRL',
            transaction_id: props.transaction_id || undefined,
          });
        }
      } else if (school3xperiencia) {
        if (conversion) {
          (window as any).gtag('event', 'conversion', {
            send_to: 'AW-10928307223',
            value: props.value,
            currency: 'BRL',
            transaction_id: props.transaction_id || undefined,
            product_name: props.product_name || undefined,
          });
        }
      }
    },
    [gaTracker, ga4Tracker]
  );

  const pageviewTracker = useCallback(
    (path: string) => {
      if (gaTracker) gaTracker.gaPageviewTrack(path);
      if (ga4Tracker) ga4Tracker.gaPageviewTrack(path);
      // if (gtmTracker) gtmTracker.gtmPageviewTrack(path);
    },
    [gaTracker, ga4Tracker]
  );

  return <TrackingContext.Provider value={{ eventTracker, pageviewTracker }}>{children}</TrackingContext.Provider>;
};

export default TrackingProvider;

// import { useAuth } from 'features/auth';
// import { useSchool } from 'features/school';
// import _ from 'lodash';
// import React, { ReactNode, useCallback, useEffect, useState } from 'react';
// import ReactGA from 'react-ga';
// import ReactGA4 from 'react-ga4';
// import TagManager from 'react-gtm-module';

// import { GaContext } from './context';
// import { EventsProps, PageviewProps } from './types';

// const GaProvider = ({ children }: GaProviderProps) => {
//   const [services, setServices] = useState<Record<string, string[]>>({});

//   // if (schoolMelver) {
//   //   ReactGA.initialize([
//   //     {
//   //       trackingId: 'GTM-TL85QLN',
//   //       gaOptions: {
//   //         name: 'gtm',
//   //       },
//   //     },
//   //     {
//   //       trackingId: 'G-EDMZSFX81D',
//   //       gaOptions: { name: 'ga' },
//   //     },
//   //   ]);
//   // }

//   const pageview = useCallback(
//     ({ path, title }: PageviewProps = {}) => {
//       setTimeout(() => {
//         if (services?.ga?.length) {
//           console.log('ga', path || window.location.pathname + window.location.search, title);
//           ReactGA.pageview(
//             path || window.location.pathname + window.location.search,
//             services?.ga?.map((_, gaIndex) => `tracker${gaIndex}`),
//             title
//           );
//         }

//         if (services?.ga4?.length) {
//           console.log('ga4', { hitType: 'pageview', page: path || window.location.pathname + window.location.search, title });
//           ReactGA4.send({ hitType: 'pageview', page: path || window.location.pathname + window.location.search, title });
//         }
//       }, 1000);
//     },
//     [services]
//   );

//   const event = ({ category, action, label, value, nonInteraction, transport }: EventsProps) => {
//     // console.log({
//     //   category,
//     //   action,
//     //   label,
//     //   value,
//     //   nonInteraction,
//     //   transport,
//     // });
//   };

//   useEffect(() => {
//     if (services?.ga?.length) {
//       console.log('initiou');
//       ReactGA.initialize(
//         services?.ga?.map((trackingId, gaIndex) => ({
//           trackingId,
//           gaOptions: {
//             name: `tracker${gaIndex}`,
//           },
//         }))
//       );
//     }

//     if (services?.ga4?.length) {
//       ReactGA4.initialize(
//         services?.ga4?.map((trackingId) => ({
//           trackingId,
//         }))
//       );
//     }

//     if (services?.gtm?.length) {
//       services?.gtm?.forEach((gtmId, gtmIndex) => {
//         TagManager.initialize({
//           gtmId,
//           dataLayerName: `PageDataLayer${gtmIndex}`,
//         });
//       });
//     }
//   }, [services]);

//   useEffect(() => {
//     if (schoolMelver) {
//       setServices({
//         ga: ['UA-75733854-1'],
//         // ga4: ['G-EDMZSFX81D'],
//         // gtm: ['GTM-TL85QLN'],
//       });
//     }
//   }, []);

//   useEffect(() => {
//     if (studentUuid) {
//       if (services?.ga?.length) {
//         ReactGA.set(
//           { userId: studentUuid },
//           services?.ga?.map((_, gaIndex) => `tracker${gaIndex}`)
//         );
//       }
//     }
//   }, [studentUuid]);

//   return <GaContext.Provider value={{ pageview, event }}>{children}</GaContext.Provider>;
// };

// export default GaProvider;
